import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import useFormSecurity from '../../../hooks/useFormSecurity';
import { exportToPDF } from '../../../utils/exportUtils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiAlertTriangle, FiChevronLeft, FiChevronRight, FiMapPin, FiCalendar, FiUser, FiFileText, FiX, FiInfo, FiPhone, FiClock, FiUsers, FiTarget, FiHelpCircle, FiActivity, FiShield, FiTool, FiGrid, FiList, FiArrowLeft, FiLoader, FiEye, FiEyeOff, FiCheck, FiLightbulb, FiPrinter, FiDownload, FiCheckCircle, FiSend } = FiIcons;

const GuidanceTip = ({ text }) => (
  <div className="flex items-start gap-2 text-sm text-blue-700 bg-blue-50 p-3 rounded-md mb-2 border border-blue-100">
    <SafeIcon icon={FiLightbulb} className="mt-0.5 flex-shrink-0 text-blue-500" />
    <span className="text-xs leading-relaxed">{text}</span>
  </div>
);

const HMSIncidentForm = () => {
  const navigate = useNavigate();
  const { addReport, reportCategories = {}, companies } = useData();
  const { validateSubmission, HoneypotField } = useFormSecurity('hms_incident_form');
  
  const [currentStep, setCurrentStep] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [hmsFormData, setHmsFormData] = useState({
    type: '',
    category: '',
    title: '',
    description: '',
    whatHappened: '',
    whenHappened: '',
    whereHappened: '',
    whoInvolved: '',
    howHappened: '',
    whyHappened: '',
    consequencesObserved: '',
    immediateActionsDescription: '',
    location: '',
    dateTime: '',
    severity: 'medium',
    isAnonymous: false,
    reporterName: '',
    reporterEmail: '',
    reporterPhone: '',
    reportedPerson: '',
    witnesses: '',
    injuredPersons: '',
    medicalAttention: false,
    emergencyServices: false,
    workStopped: false,
    immediateActions: '',
    rootCause: '',
    preventiveMeasures: '',
    companyId: null
  });

  useEffect(() => {
    setHmsFormData(prev => ({ ...prev, companyId: sessionStorage.getItem('hmsCompanyId') || null }));
  }, []);

  const handleCategorySelect = (categoryId, sectionId) => {
    setHmsFormData(prev => ({ ...prev, type: categoryId, category: sectionId }));
    setCurrentStep(2);
    setShowValidationErrors(false);
  };

  const handleInputChange = (field, value) => {
    setHmsFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGoBack = () => {
    navigate('/hms');
  };

  const handleDownloadPDF = async () => {
    setIsGenerating(true);
    try {
      await exportToPDF(hmsFormData, 'hms_incident');
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert('Det oppstod en feil ved generering av PDF.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validateSubmission()) return;
    if (isSubmitting) return;

    const requiredFields = {
      title: hmsFormData.title,
      whatHappened: hmsFormData.whatHappened,
      type: hmsFormData.type,
      dateTime: hmsFormData.dateTime,
      whereHappened: hmsFormData.whereHappened
    };

    if (!hmsFormData.isAnonymous) {
      requiredFields.reporterName = hmsFormData.reporterName;
    }

    const missingFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([key]) => key);

    if (missingFields.length > 0) {
      setShowValidationErrors(true);
      alert(`Vennligst fyll ut alle obligatoriske felt.`);
      return;
    }

    let companyId = hmsFormData.companyId || sessionStorage.getItem('hmsCompanyId');
    if (!companyId && companies && companies.length > 0) {
      companyId = companies[0].id;
    }

    if (!companyId) {
      alert('Bedriftsinformasjon mangler. Vennligst start fra HMS dashboard.');
      return;
    }

    setIsSubmitting(true);
    try {
      const reportData = {
        ...hmsFormData,
        companyId: companyId,
        description: hmsFormData.whatHappened,
        location: hmsFormData.whereHappened,
        isAnonymous: hmsFormData.isAnonymous,
        reporterName: hmsFormData.isAnonymous ? 'Anonym' : hmsFormData.reporterName,
        reporterEmail: hmsFormData.isAnonymous ? null : hmsFormData.reporterEmail,
        reporterPhone: hmsFormData.isAnonymous ? null : hmsFormData.reporterPhone,
        reportType: 'hms_incident'
      };

      const newReport = addReport(reportData);
      
      navigate('/hms/confirmation', { 
        state: { 
          reportId: newReport.id, 
          isAnonymous: hmsFormData.isAnonymous,
          reportType: 'hms_incident'
        } 
      });
    } catch (error) {
      console.error('Error submitting HMS report:', error);
      alert('Det oppstod en feil ved innsending: ' + error.message);
      setIsSubmitting(false);
    }
  };

  const canProceedToNextStep = (step) => {
    switch (step) {
      case 1: return hmsFormData.type && hmsFormData.category;
      case 2: return hmsFormData.title && hmsFormData.whatHappened && hmsFormData.whereHappened && hmsFormData.dateTime;
      case 3: return true;
      case 4: return true;
      case 5: return hmsFormData.isAnonymous || (hmsFormData.reporterName && hmsFormData.reporterEmail);
      default: return true;
    }
  };

  const validateAndProceed = (nextStep) => {
    if (canProceedToNextStep(currentStep)) {
      setCurrentStep(nextStep);
      setShowValidationErrors(false);
      window.scrollTo(0, 0);
    } else {
      setShowValidationErrors(true);
      setTimeout(() => {
        const firstError = document.querySelector('.border-red-500');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const getInputClass = (fieldName) => {
    const hasError = showValidationErrors && !hmsFormData[fieldName];
    return `w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasError ? 'border-red-500 bg-red-50' : 'border-gray-300'}`;
  };

  const ErrorMessage = () => (
    <p className="text-red-600 text-sm mt-1 flex items-center animate-pulse">
      <SafeIcon icon={FiAlertTriangle} className="mr-1" /> Må fylles ut
    </p>
  );

  const steps = [
    { id: 1, title: 'Type hendelse', description: 'Velg hendelseskategori' },
    { id: 2, title: 'Hendelsesdetaljer', description: 'Beskriv hva som skjedde' },
    { id: 3, title: 'Skadeomfang', description: 'Personer og konsekvenser' },
    { id: 4, title: 'Tiltak og årsak', description: 'Umiddelbare tiltak og årsaksanalyse' },
    { id: 5, title: 'Rapportør', description: 'Dine kontaktopplysninger' },
    { id: 6, title: 'Forhåndsvisning', description: 'Se gjennom og send' }
  ];

  const severityOptions = [
    { value: 'low', label: 'Lav', color: 'text-green-600', description: 'Mindre hendelse uten skade' },
    { value: 'medium', label: 'Middels', color: 'text-yellow-600', description: 'Hendelse med potensial for skade' },
    { value: 'high', label: 'Høy', color: 'text-orange-600', description: 'Alvorlig hendelse med skade' },
    { value: 'critical', label: 'Kritisk', color: 'text-red-600', description: 'Livstruende eller alvorlig skade' }
  ];

  const emergencyServices = [
    { number: '113', service: 'Medisinsk nødtelefon', description: 'Ved personskader og medisinske nødsituasjoner', color: 'bg-red-500', icon: FiActivity },
    { number: '110', service: 'Brannvesenet', description: 'Ved brann, eksplosjon og akutte faresituasjoner', color: 'bg-orange-500', icon: FiAlertTriangle },
    { number: '112', service: 'Politi', description: 'Ved kriminalitet og alvorlige sikkerhetshendelser', color: 'bg-blue-500', icon: FiShield }
  ];

  const safeCategories = (reportCategories && Object.keys(reportCategories).length > 0) 
    ? reportCategories 
    : { hms: { label: 'HMS & Sikkerhet', color: 'red', categories: [{ id: 'fallback', name: 'Laster kategorier...', description: 'Vennligst vent' }] } };

  const PreviewField = ({ label, value, fullWidth = false }) => (
    <div className={`${fullWidth ? 'md:col-span-2' : ''} border-b border-gray-100 pb-3`}>
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm text-gray-800 font-medium whitespace-pre-wrap">{value || '-'}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <HoneypotField />
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiAlertTriangle} className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">HMS Hendelsesrapport</h1>
                <p className="text-sm text-gray-600">
                  {sessionStorage.getItem('hmsCompanyName') || 'Rapporter HMS-hendelse'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {currentStep === 1 && (
                <button type="button" onClick={handleGoBack} className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors flex items-center space-x-2" >
                  <SafeIcon icon={FiArrowLeft} /> <span>Tilbake</span>
                </button>
              )}
            </div>
          </div>
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4 overflow-x-auto pb-2">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center flex-shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${currentStep >= step.id ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                    {step.id}
                  </div>
                  <div className="ml-3 hidden md:block">
                    <p className={`text-sm font-medium ${currentStep >= step.id ? 'text-red-600' : 'text-gray-500'}`}>
                      {step.title}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-4 md:w-8 h-0.5 mx-2 md:mx-4 ${currentStep > step.id ? 'bg-red-600' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          {/* Step 1: Category Selection */}
          {currentStep === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="space-y-6" >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Hvilken type hendelse rapporterer du?</h2>
                <div className="flex items-center justify-center space-x-4 mt-6">
                  <span className="text-sm text-gray-600">Visning:</span>
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button type="button" onClick={() => setViewMode('grid')} className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'grid' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} ><SafeIcon icon={FiGrid} /> <span>Rutenett</span></button>
                    <button type="button" onClick={() => setViewMode('list')} className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} ><SafeIcon icon={FiList} /> <span>Liste</span></button>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 mb-8">
                <div className="text-center mb-6">
                  <h3 className="text-4xl font-bold text-red-900 mb-4">🚨 Ved akutte nødsituasjoner</h3>
                  <p className="text-red-800 text-xl font-semibold"><strong>Ring først - rapporter senere!</strong></p>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                  {emergencyServices.map((service, index) => (
                    <motion.div key={service.number} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.1 }} className="bg-white border-2 border-gray-200 rounded-xl p-8 text-center hover:shadow-lg transition-all duration-300" >
                      <div className={`w-20 h-20 ${service.color} rounded-full flex items-center justify-center mx-auto mb-4`}><SafeIcon icon={service.icon} className="text-white text-3xl" /></div>
                      <div className="text-5xl font-bold text-gray-900 mb-3">{service.number}</div>
                      <h4 className="font-semibold text-gray-900 mb-3 text-lg">{service.service}</h4>
                      <p className="text-sm text-gray-600 mb-6">{service.description}</p>
                      <a href={`tel:${service.number}`} className={`inline-block ${service.color} text-white px-8 py-4 rounded-lg hover:opacity-90 transition-opacity font-medium text-lg`}>Ring {service.number}</a>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className={viewMode === 'grid' ? 'grid gap-6' : 'space-y-6'}>
                {Object.entries(safeCategories).map(([sectionId, section]) => (
                  <div key={sectionId} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className={`p-6 text-center border-l-8 ${section.color === 'red' ? 'border-red-500 bg-red-50' : section.color === 'green' ? 'border-green-500 bg-green-50' : section.color === 'blue' ? 'border-blue-500 bg-blue-50' : section.color === 'orange' ? 'border-orange-500 bg-orange-50' : section.color === 'purple' ? 'border-purple-500 bg-purple-50' : 'border-gray-500 bg-gray-50'}`}>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{section.label}</h3>
                      <p className="text-sm text-gray-600">{section.categories.length} kategorier tilgjengelig</p>
                    </div>
                    <div className={viewMode === 'grid' ? 'p-6 grid md:grid-cols-2 gap-4' : 'p-6 space-y-4'}>
                      {section.categories.map((category) => (
                        <motion.button key={category.id} type="button" onClick={() => handleCategorySelect(category.id, sectionId)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full text-left p-6 border-2 border-gray-200 rounded-xl hover:border-red-300 hover:bg-red-50 transition-colors group" >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 group-hover:text-red-900 mb-3 text-lg">{category.name}</h4>
                              <p className="text-sm text-gray-600 mb-4">{category.description}</p>
                            </div>
                            <div className="ml-4 flex flex-col items-end space-y-2">
                              <SafeIcon icon={FiChevronRight} className="text-gray-400 group-hover:text-red-600 text-2xl" />
                            </div>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Incident Details */}
          {currentStep === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="space-y-6" >
              <div className="text-center mb-8"><h2 className="text-2xl font-bold text-gray-900 mb-2">Beskriv hendelsen</h2></div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-8">
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Hendelsesdetaljer</h3>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <label className="block text-sm font-semibold text-gray-900 mb-1">Kort tittel på hendelsen *</label>
                    <GuidanceTip text="Gi hendelsen et kort navn som beskriver hva som skjedde, f.eks. 'Fall i trapp', 'Kjemikaliesøl på lab' eller 'Defekt verneutstyr'." />
                    <input type="text" value={hmsFormData.title} onChange={(e) => handleInputChange('title', e.target.value)} className={getInputClass('title')} required />
                    {showValidationErrors && !hmsFormData.title && <ErrorMessage />}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <label className="block text-sm font-semibold text-gray-900 mb-1">Hva skjedde? *</label>
                    <GuidanceTip text="Beskriv hendelsesforløpet objektivt. Hva observerte du? Hva gjorde du? Unngå å spekulere i skyld, hold deg til fakta." />
                    <textarea value={hmsFormData.whatHappened} onChange={(e) => handleInputChange('whatHappened', e.target.value)} rows="5" className={getInputClass('whatHappened')} required />
                    {showValidationErrors && !hmsFormData.whatHappened && <ErrorMessage />}
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-lg p-6">
                      <label className="block text-sm font-semibold text-gray-900 mb-1">Når skjedde det? *</label>
                      <GuidanceTip text="Angi dato og klokkeslett så nøyaktig som mulig." />
                      <input type="datetime-local" value={hmsFormData.dateTime} onChange={(e) => handleInputChange('dateTime', e.target.value)} className={getInputClass('dateTime')} required />
                      {showValidationErrors && !hmsFormData.dateTime && <ErrorMessage />}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-6">
                      <label className="block text-sm font-semibold text-gray-900 mb-1">Hvor skjedde det? *</label>
                      <GuidanceTip text="Vær spesifikk. Angi bygg, etasje, romnummer, eller spesifikt område." />
                      <input type="text" value={hmsFormData.whereHappened} onChange={(e) => handleInputChange('whereHappened', e.target.value)} className={getInputClass('whereHappened')} required />
                      {showValidationErrors && !hmsFormData.whereHappened && <ErrorMessage />}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">Alvorlighetsgrad *</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {severityOptions.map((option) => (
                        <button key={option.value} type="button" onClick={() => handleInputChange('severity', option.value)} className={`p-4 border rounded-lg text-center transition-colors ${hmsFormData.severity === option.value ? 'border-red-500 bg-red-50 text-red-900' : 'border-gray-200 hover:border-gray-300'}`} >
                          <div className={`font-medium ${option.color} mb-1`}>{option.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between">
                  <button type="button" onClick={() => setCurrentStep(1)} className="flex items-center space-x-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"><SafeIcon icon={FiChevronLeft} /> <span>Tilbake</span></button>
                  <button type="button" onClick={() => validateAndProceed(3)} className="flex items-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"><span>Neste</span> <SafeIcon icon={FiChevronRight} /></button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Injuries */}
          {currentStep === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="space-y-6" >
              <div className="text-center mb-8"><h2 className="text-2xl font-bold text-gray-900 mb-2">Skader og konsekvenser</h2></div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Skadde personer</label>
                  <textarea value={hmsFormData.injuredPersons} onChange={(e) => handleInputChange('injuredPersons', e.target.value)} rows="3" className="w-full border border-gray-300 rounded-lg px-4 py-3" />
                </div>
                <div className="flex items-center space-x-3"><input type="checkbox" id="medicalAttention" checked={hmsFormData.medicalAttention} onChange={(e) => handleInputChange('medicalAttention', e.target.checked)} className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500" /><label htmlFor="medicalAttention" className="text-sm font-medium text-gray-700">Medisinsk behandling var nødvendig</label></div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vitner til hendelsen</label>
                  <textarea value={hmsFormData.witnesses} onChange={(e) => handleInputChange('witnesses', e.target.value)} rows="3" className="w-full border border-gray-300 rounded-lg px-4 py-3" />
                </div>
              </div>
              <div className="flex justify-between">
                <button type="button" onClick={() => setCurrentStep(2)} className="flex items-center space-x-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"><SafeIcon icon={FiChevronLeft} /> <span>Tilbake</span></button>
                <button type="button" onClick={() => setCurrentStep(4)} className="flex items-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"><span>Neste</span> <SafeIcon icon={FiChevronRight} /></button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Actions */}
          {currentStep === 4 && (
            <motion.div key="step4" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="space-y-6" >
              <div className="text-center mb-8"><h2 className="text-2xl font-bold text-gray-900 mb-2">Tiltak og årsaksanalyse</h2></div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Umiddelbare tiltak</label>
                  <textarea value={hmsFormData.immediateActions} onChange={(e) => handleInputChange('immediateActions', e.target.value)} rows="4" className="w-full border border-gray-300 rounded-lg px-4 py-3" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mulige årsaker</label>
                  <textarea value={hmsFormData.rootCause} onChange={(e) => handleInputChange('rootCause', e.target.value)} rows="4" className="w-full border border-gray-300 rounded-lg px-4 py-3" />
                </div>
              </div>
              <div className="flex justify-between">
                <button type="button" onClick={() => setCurrentStep(3)} className="flex items-center space-x-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"><SafeIcon icon={FiChevronLeft} /> <span>Tilbake</span></button>
                <button type="button" onClick={() => setCurrentStep(5)} className="flex items-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"><span>Neste</span> <SafeIcon icon={FiChevronRight} /></button>
              </div>
            </motion.div>
          )}

          {/* Step 5: Reporter */}
          {currentStep === 5 && (
            <motion.div key="step5" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className="space-y-6" >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Dine kontaktopplysninger</h2>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
                <div className={`rounded-lg p-4 transition-colors ${hmsFormData.isAnonymous ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-full ${hmsFormData.isAnonymous ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-600'}`}>
                        <SafeIcon icon={hmsFormData.isAnonymous ? FiEyeOff : FiEye} className="text-xl" />
                      </div>
                      <div>
                        <h3 className={`font-bold ${hmsFormData.isAnonymous ? 'text-blue-900' : 'text-gray-900'}`}>
                          {hmsFormData.isAnonymous ? 'Jeg sender inn anonymt' : 'Jeg sender med fullt navn'}
                        </h3>
                      </div>
                    </div>
                    <button type="button" onClick={() => handleInputChange('isAnonymous', !hmsFormData.isAnonymous)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${hmsFormData.isAnonymous ? 'bg-blue-600' : 'bg-gray-200'}`} >
                      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${hmsFormData.isAnonymous ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </div>
                </div>
                {!hmsFormData.isAnonymous && (
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fullt navn *</label>
                      <input type="text" value={hmsFormData.reporterName} onChange={(e) => handleInputChange('reporterName', e.target.value)} className={getInputClass('reporterName')} required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">E-postadresse *</label>
                      <input type="email" value={hmsFormData.reporterEmail} onChange={(e) => handleInputChange('reporterEmail', e.target.value)} className={getInputClass('reporterEmail')} required />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-between">
                <button type="button" onClick={() => setCurrentStep(4)} className="flex items-center space-x-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"><SafeIcon icon={FiChevronLeft} /> <span>Tilbake</span></button>
                <button type="button" onClick={() => validateAndProceed(6)} className="flex items-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"><span>Se Forhåndsvisning</span> <SafeIcon icon={FiChevronRight} /></button>
              </div>
            </motion.div>
          )}

          {/* Step 6: Preview */}
          {currentStep === 6 && (
            <motion.div key="step6" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }} className="space-y-6" >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Forhåndsvisning av HMS-rapport</h2>
                <p className="text-gray-600">Vennligst se gjennom rapporten før du sender den inn.</p>
              </div>

              <div className="bg-white shadow-2xl rounded-sm border border-gray-200 overflow-hidden max-w-4xl mx-auto flex flex-col font-serif min-h-[800px]">
                <div className="bg-red-600 h-2 w-full"></div>
                <div className="p-12 space-y-10">
                  <div className="flex justify-between items-start border-b-2 border-gray-900 pb-8">
                    <div>
                      <h1 className="text-4xl font-black text-gray-900 tracking-tighter uppercase mb-2">HMS-Hendelsesrapport</h1>
                      <div className="flex items-center gap-4 text-sm text-gray-500 font-sans">
                        <span className="flex items-center gap-1"><SafeIcon icon={FiCalendar} /> {new Date().toLocaleDateString('nb-NO')}</span>
                        <span className="flex items-center gap-1 font-mono uppercase">ID: HMS-PROT-{Date.now().toString().slice(-6)}</span>
                      </div>
                    </div>
                    <div className="text-right font-sans">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg ml-auto mb-2 flex items-center justify-center text-gray-400">
                        <SafeIcon icon={FiShield} size={32} />
                      </div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Sikker Innsending</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 font-sans">
                    <section className="space-y-6">
                      <h3 className="text-xs font-black text-red-600 uppercase tracking-[0.2em] border-b border-red-100 pb-2">1. Om Hendelsen</h3>
                      <div className="space-y-4">
                        <PreviewField label="Tittel" value={hmsFormData.title} />
                        <PreviewField label="Kategori" value={`${safeCategories[hmsFormData.category]?.label} - ${safeCategories[hmsFormData.category]?.categories.find(c => c.id === hmsFormData.type)?.name}`} />
                        <PreviewField label="Alvorlighetsgrad" value={hmsFormData.severity.toUpperCase()} />
                        <PreviewField label="Tidspunkt" value={new Date(hmsFormData.dateTime).toLocaleString('nb-NO')} />
                        <PreviewField label="Lokasjon" value={hmsFormData.whereHappened} />
                      </div>
                    </section>

                    <section className="space-y-6">
                      <h3 className="text-xs font-black text-red-600 uppercase tracking-[0.2em] border-b border-red-100 pb-2">2. Involverte & Vitner</h3>
                      <div className="space-y-4">
                        <PreviewField label="Involverte parter" value={hmsFormData.whoInvolved} />
                        <PreviewField label="Skadde personer" value={hmsFormData.injuredPersons} />
                        <PreviewField label="Vitner" value={hmsFormData.witnesses} />
                        <div className="flex gap-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded border ${hmsFormData.medicalAttention ? 'bg-red-500 border-red-600' : 'bg-gray-100 border-gray-300'} flex items-center justify-center`}>
                              {hmsFormData.medicalAttention && <SafeIcon icon={FiCheck} className="text-white text-[10px]" />}
                            </div>
                            <span className="text-[11px] font-bold text-gray-600 uppercase">Medisinsk hjelp</span>
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="md:col-span-2 space-y-6">
                      <h3 className="text-xs font-black text-red-600 uppercase tracking-[0.2em] border-b border-red-100 pb-2">3. Detaljert Beskrivelse</h3>
                      <div className="bg-gray-50 p-6 rounded-lg text-gray-700 leading-relaxed italic border border-gray-100">
                        {hmsFormData.whatHappened}
                      </div>
                    </section>

                    <section className="md:col-span-2 space-y-6">
                      <h3 className="text-xs font-black text-red-600 uppercase tracking-[0.2em] border-b border-red-100 pb-2">4. Tiltak & Årsak</h3>
                      <div className="grid md:grid-cols-2 gap-8">
                        <PreviewField label="Umiddelbare tiltak" value={hmsFormData.immediateActions} />
                        <PreviewField label="Vurdert årsak" value={hmsFormData.rootCause} />
                      </div>
                    </section>

                    <section className="md:col-span-2 space-y-6">
                      <h3 className="text-xs font-black text-red-600 uppercase tracking-[0.2em] border-b border-red-100 pb-2">5. Rapportør</h3>
                      <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-xl">
                        {hmsFormData.isAnonymous ? (
                          <div className="flex items-center gap-3 text-blue-600">
                            <SafeIcon icon={FiEyeOff} size={24} />
                            <span className="font-black uppercase tracking-widest text-sm">Anonym Rapportering</span>
                          </div>
                        ) : (
                          <div className="grid md:grid-cols-3 gap-8 w-full">
                            <PreviewField label="Navn" value={hmsFormData.reporterName} />
                            <PreviewField label="E-post" value={hmsFormData.reporterEmail} />
                            <PreviewField label="Telefon" value={hmsFormData.reporterPhone} />
                          </div>
                        )}
                      </div>
                    </section>
                  </div>

                  <div className="pt-12 mt-12 border-t border-gray-100 flex justify-between items-end">
                    <div className="text-[10px] text-gray-400 font-sans space-y-1">
                      <p>Dette dokumentet er generert i HMSVarsling-portalen.</p>
                      <p>All data behandles konfidensielt i henhold til GDPR.</p>
                    </div>
                    <div className="text-right">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 text-4xl ml-auto mb-2" />
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Klar for innsending</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center max-w-4xl mx-auto pt-8">
                <button type="button" onClick={() => setCurrentStep(5)} className="flex items-center space-x-2 px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors font-bold" disabled={isSubmitting} >
                  <SafeIcon icon={FiChevronLeft} /> <span>Rediger mer</span>
                </button>
                <div className="flex gap-4">
                  <button type="button" onClick={() => window.print()} className="flex items-center space-x-2 px-4 py-3 border-2 border-gray-800 rounded-xl text-gray-800 hover:bg-gray-100 transition-colors font-bold" >
                    <SafeIcon icon={FiPrinter} /> <span>Print kopi</span>
                  </button>
                  
                  {/* NY LAST NED KNAPP */}
                  <button type="button" onClick={handleDownloadPDF} disabled={isGenerating} className="flex items-center space-x-2 px-4 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition-colors font-bold shadow-sm">
                    {isGenerating ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiDownload} />}
                    <span>Last ned PDF</span>
                  </button>

                  <button type="button" onClick={handleSubmit} disabled={isSubmitting} className={`flex items-center space-x-2 px-10 py-3 rounded-xl text-white transition-all font-black uppercase tracking-widest shadow-xl hvr-grow ${isSubmitting ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`} >
                    {isSubmitting ? (
                      <> <SafeIcon icon={FiLoader} className="animate-spin" /> <span>Sender...</span> </>
                    ) : (
                      <> <SafeIcon icon={FiSend} /> <span>Bekreft & Send Rapport</span> </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default HMSIncidentForm;